import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { DateRangeParams, statusOrder } from '../types';
import './date-range-selector.css';

interface DateRangeSelectorProps {
  onSearch: (params: DateRangeParams) => void;
  loading: boolean;
}

interface StatusOption {
  label: string;
  value: statusOrder;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ onSearch, loading }) => {
  const [dateRange, setDateRange] = useState<Date[] | null>([]);
  const [selectedStatus, setSelectedStatus] = useState<statusOrder>(statusOrder.PENDING);
  const initialSearchDone = useRef(false);

  // Inicializar con los últimos 30 días
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 31);
    
    setDateRange([thirtyDaysAgo, today]);
  }, []);

  // Opciones de status basadas en el enum statusOrder
  const statusOptions: StatusOption[] = [
    { label: 'Pendiente', value: statusOrder.PENDING },
    { label: 'Verificado', value: statusOrder.VERIFIED }
  ];

  // Memoizar la función de búsqueda para evitar recreaciones
  const handleSearch = useCallback(() => {
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      
      // Preparar parámetros de búsqueda
      const searchParams: DateRangeParams = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        status: [selectedStatus]
      };
      
      // Enviar parámetros
      onSearch(searchParams);
    }
  }, [dateRange, selectedStatus, onSearch]);

  // Ejecutar búsqueda automáticamente al inicializar el componente
  useEffect(() => {
    if (dateRange && dateRange.length === 2 && !initialSearchDone.current) {
      handleSearch();
      initialSearchDone.current = true;
    }
  }, [dateRange, handleSearch]);

  return (
    <div className="date-range-selector">
      <div className="calendar-container">
        <span className="p-float-label">
          <Calendar 
            value={dateRange} 
            onChange={(e) => setDateRange(e.value as Date[])} 
            selectionMode="range" 
            readOnlyInput
            showIcon
            dateFormat="dd/mm/yy"
            id="dateRange"
          />
        </span>
      </div>
      
      <div className="status-dropdown-container">
        <Dropdown
          value={selectedStatus}
          options={statusOptions}
          onChange={(e) => setSelectedStatus(e.value)}
          optionLabel="label"
          placeholder="Seleccionar estado"
          className="status-dropdown"
        />
      </div>
      
      <Button 
        label="Buscar" 
        icon="pi pi-search" 
        onClick={handleSearch} 
        loading={loading}
      />
    </div>
  );
};

export default DateRangeSelector;
